import { useState, useEffect, useCallback } from "react";
import { Table, Input, Typography, Modal, Image, message } from "antd";
import blink from "../../assets/images/blink.png";
import { Contact } from "../../types/contact";
import { ListResponse } from "../../types";
import { contactService } from "../../services";
import { Pagination } from "../../types";
import { formatDateDetail } from "../../helpers";

const { Text } = Typography;

const ContactsManagement = () => {
  const columns = [
    {
      title: "Họ tên",
      dataIndex: "fullName",
      key: "fullName",
      render: (fullName: string) => (
        <Text style={{ fontWeight: 500 }}>{fullName}</Text>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email: string) => (
        <a href={`mailto:${email}`} style={{ color: "#1890ff" }}>
          {email}
        </a>
      ),
    },
    {
      title: "Số điện thoại",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Tiêu đề",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Lời nhắn",
      dataIndex: "content",
      key: "content",
      render: (content: string, record: Contact) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Text>{truncateMessage(content)}</Text>
          {content.length > 50 && (
            <Text
              style={{ cursor: "pointer" }}
              onClick={() => handlePreview(record)}
            >
              Xem chi tiết
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Ngày gửi",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (sentTime: string) => <Text>{formatDateDetail(sentTime)}</Text>,
    },
  ];

  const [searchText, setSearchText] = useState("");
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [contactsData, setContactsData] = useState<ListResponse<Contact>>();
  const [isLoading, setIsLoading] = useState(false);
  const [contactsPagination, setContactsPagination] = useState<Pagination>({
    page: 1,
    resultsPerPage: 10,
  });

  const getContactList = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await contactService.getContactList(contactsPagination);
      setContactsData(response);
    } catch (error) {
      message.error("Lỗi khi lấy danh sách liên hệ: " + error);
    } finally {
      setIsLoading(false);
    }
  }, [contactsPagination]);

  useEffect(() => {
    getContactList();
  }, [getContactList]);

  const handlePreview = (record: Contact) => {
    setPreviewContent(record.content);
    setIsPreviewVisible(true);
  };

  const truncateMessage = (message: string, maxLength = 50) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + "...";
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setContactsPagination({
      ...contactsPagination,
      page,
      resultsPerPage: pageSize,
    });
  };

  return (
    <div style={{ padding: "20px" }}>
      <h1 className="page-title">
        Quản lý liên hệ <Image src={blink} preview={false} className="blink-icon" />
      </h1>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Input.Search
          placeholder="Tìm kiếm"
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: 500 }}
          className="contact-search-input"
        />
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={contactsData ? contactsData.data : []}
        pagination={{
          current: contactsPagination.page,
          pageSize: contactsPagination.resultsPerPage,
          total: contactsData ? contactsData.totalResults : 0,
          onChange: handlePaginationChange,
        }}
        loading={isLoading}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Message Preview"
        open={isPreviewVisible}
        onCancel={() => setIsPreviewVisible(false)}
        footer={null}
        width={600}
      >
        <div
          style={{
            padding: "16px",
            backgroundColor: "#f8f9fa",
            borderRadius: "6px",
            border: "1px solid #e9ecef",
            whiteSpace: "pre-wrap",
            lineHeight: "1.6",
          }}
        >
          {previewContent}
        </div>
      </Modal>
    </div>
  );
};

export default ContactsManagement;
